import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'en' | 'es' | 'fr' | 'hi' | 'ta' | 'mai';

interface LanguageContextType {
  language: Language;
  changeLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  en: {
    // Navbar
    features: 'Features',
    farming_techniques: 'Farming Techniques',
    soil_info: 'Soil Information',
    marketplace: 'Buy/Sell Platform',
    contact: 'Contact',
    login: 'Login',
    register: 'Register',
    language_selector: 'Language',
    english: 'English',
    spanish: 'Spanish',
    french: 'French',
    hindi: 'Hindi',
    tamil: 'Tamil',
    maithili: 'Maithili',
    profile: 'Profile',
    logout: 'Logout',

    // Hero
    hero_title: 'Welcome to GreenIQ',
    hero_subtitle: 'Your trusted partner in modern agriculture. Get insights, use our tools, and grow smarter.',
    learn_more: 'Learn More',

    // About
    about_title: 'About Crops & Agriculture',
    about_text: 'Agriculture is the backbone of our society, providing food, raw materials, and employment to millions. Modern agriculture combines traditional wisdom with cutting-edge technology to improve yields, sustainability, and food security. At GreenIQ, we empower farmers with AI-driven insights, crop management tools, and a vibrant marketplace.',
    about_list_1: 'Learn about different crops and their requirements',
    about_list_2: 'Discover sustainable farming techniques',
    about_list_3: 'Connect with experts and fellow farmers',

    // Features
    platform_features: 'Platform Features',
    ai_assistant: 'AI Assistant',
    ai_assistant_desc: 'Get personalized advice and answers to your farming questions from our AI-powered assistant.',
    marketplace_title: 'Marketplace',
    marketplace_desc: 'A platform to buy and sell your produce directly to consumers and businesses.',

    // Weather
    weather: 'Weather',
    search_city: 'Search for a city...',
    temperature: 'Temperature',
    humidity: 'Humidity',
    wind: 'Wind Speed',
    aqi: 'Air Quality',

    // News
    agri_news: 'Agri News',
    loading_news: 'Loading latest real-time news...',

    // Auth
    email: 'Email',
    password: 'Password',
    name: 'Name',
    phone: 'Phone',
    occupation: 'Occupation',
    login_button: 'Login',
    register_button: 'Register',
    new_user: 'New user? Register here',
    existing_user: 'Already have an account? Login',
  },
  es: {
    features: 'Características',
    farming_techniques: 'Técnicas de Agricultura',
    soil_info: 'Información del Suelo',
    marketplace: 'Plataforma de Compra/Venta',
    contact: 'Contacto',
    login: 'Iniciar Sesión',
    register: 'Registrarse',
    language_selector: 'Idioma',
    english: 'Inglés',
    spanish: 'Español',
    french: 'Francés',
    hindi: 'Hindi',
    tamil: 'Tamil',
    maithili: 'Maithili',
    profile: 'Perfil',
    logout: 'Cerrar Sesión',
    hero_title: 'Bienvenido a GreenIQ',
    hero_subtitle: 'Tu socio de confianza en la agricultura moderna. Obtén información, usa nuestras herramientas y crece inteligentemente.',
    learn_more: 'Aprender Más',
    about_title: 'Acerca de Cultivos y Agricultura',
    about_text: 'La agricultura es la columna vertebral de nuestra sociedad, proporcionando alimentos, materias primas y empleo a millones de personas.',
    about_list_1: 'Aprender sobre diferentes cultivos y sus requisitos',
    about_list_2: 'Descubrir técnicas de agricultura sostenible',
    about_list_3: 'Conectar con expertos y otros agricultores',
    platform_features: 'Características de la Plataforma',
    ai_assistant: 'Asistente de IA',
    ai_assistant_desc: 'Obtén consejos personalizados y respuestas a tus preguntas sobre agricultura de nuestro asistente impulsado por IA.',
    marketplace_title: 'Mercado',
    marketplace_desc: 'Una plataforma para comprar y vender sus productos directamente a consumidores y empresas.',
    weather: 'Clima',
    search_city: 'Buscar una ciudad...',
    temperature: 'Temperatura',
    humidity: 'Humedad',
    wind: 'Velocidad del Viento',
    aqi: 'Calidad del Aire',
    agri_news: 'Noticias Agri',
    loading_news: 'Cargando últimas noticias en tiempo real...',
    email: 'Correo Electrónico',
    password: 'Contraseña',
    name: 'Nombre',
    phone: 'Teléfono',
    occupation: 'Ocupación',
    login_button: 'Iniciar Sesión',
    register_button: 'Registrarse',
    new_user: '¿Usuario nuevo? Registrate aquí',
    existing_user: '¿Ya tienes cuenta? Inicia sesión',
  },
  fr: {
    features: 'Caractéristiques',
    farming_techniques: 'Techniques Agricoles',
    soil_info: 'Information sur le Sol',
    marketplace: 'Plateforme d\'Achat/Vente',
    contact: 'Contact',
    login: 'Connexion',
    register: 'S\'inscrire',
    language_selector: 'Langue',
    english: 'Anglais',
    spanish: 'Espagnol',
    french: 'Français',
    hindi: 'Hindi',
    tamil: 'Tamoul',
    maithili: 'Maithili',
    profile: 'Profil',
    logout: 'Déconnexion',
    hero_title: 'Bienvenue sur GreenIQ',
    hero_subtitle: 'Votre partenaire de confiance en agriculture moderne. Obtenez des informations, utilisez nos outils et grandissez intelligemment.',
    learn_more: 'En Savoir Plus',
    about_title: 'À Propos des Cultures et de l\'Agriculture',
    about_text: 'L\'agriculture est l\'épine dorsale de notre société, fournissant de la nourriture, des matières premières et de l\'emploi à des millions de personnes.',
    about_list_1: 'En savoir plus sur différentes cultures et leurs exigences',
    about_list_2: 'Découvrez des techniques d\'agriculture durable',
    about_list_3: 'Connectez-vous avec des experts et d\'autres agriculteurs',
    platform_features: 'Caractéristiques de la Plateforme',
    ai_assistant: 'Assistant IA',
    ai_assistant_desc: 'Obtenez des conseils personnalisés et des réponses à vos questions agricoles de notre assistant alimenté par l\'IA.',
    marketplace_title: 'Marché',
    marketplace_desc: 'Une plateforme pour acheter et vendre vos produits directement aux consommateurs et aux entreprises.',
    weather: 'Météo',
    search_city: 'Rechercher une ville...',
    temperature: 'Température',
    humidity: 'Humidité',
    wind: 'Vitesse du Vent',
    aqi: 'Qualité de l\'Air',
    agri_news: 'Actualités Agri',
    loading_news: 'Chargement des dernières actualités en temps réel...',
    email: 'E-mail',
    password: 'Mot de Passe',
    name: 'Nom',
    phone: 'Téléphone',
    occupation: 'Occupation',
    login_button: 'Connexion',
    register_button: 'S\'inscrire',
    new_user: 'Nouvel utilisateur? Inscrivez-vous ici',
    existing_user: 'Vous avez déjà un compte? Connectez-vous',
  },
  hi: {
    features: 'विशेषताएं',
    farming_techniques: 'कृषि तकनीकें',
    soil_info: 'मिट्टी की जानकारी',
    marketplace: 'खरीद/बिक्री मंच',
    contact: 'संपर्क',
    login: 'लॉगिन',
    register: 'पंजीकरण',
    language_selector: 'भाषा',
    english: 'अंग्रेजी',
    spanish: 'स्पेनिश',
    french: 'फ्रेंच',
    hindi: 'हिंदी',
    tamil: 'तमिल',
    maithili: 'मैथिली',
    profile: 'प्रोफाइल',
    logout: 'लॉगआउट',
    hero_title: 'GreenIQ में आपका स्वागत है',
    hero_subtitle: 'आधुनिक कृषि में आपका विश्वसनीय साथी। अंतर्दृष्टि प्राप्त करें, हमारे उपकरण का उपयोग करें और स्मार्ट तरीके से बढ़ें।',
    learn_more: 'और जानें',
    about_title: 'फसलों और कृषि के बारे में',
    about_text: 'कृषि हमारे समाज की रीढ़ है, जो लाखों लोगों को भोजन, कच्चे माल और रोजगार प्रदान करती है।',
    about_list_1: 'विभिन्न फसलों और उनकी आवश्यकताओं के बारे में जानें',
    about_list_2: 'टिकाऊ कृषि तकनीकों की खोज करें',
    about_list_3: 'विशेषज्ञों और अन्य किसानों से जुड़ें',
    platform_features: 'मंच की विशेषताएं',
    ai_assistant: 'एआई सहायक',
    ai_assistant_desc: 'हमारे AI-संचालित सहायक से आपके कृषि प्रश्नों के लिए व्यक्तिगत सलाह और उत्तर प्राप्त करें।',
    marketplace_title: 'बाजार',
    marketplace_desc: 'अपनी उपज को सीधे उपभोक्ताओं और व्यवसायों को खरीदने और बेचने के लिए एक मंच।',
    weather: 'मौसम',
    search_city: 'एक शहर खोजें...',
    temperature: 'तापमान',
    humidity: 'नमी',
    wind: 'हवा की गति',
    aqi: 'वायु गुणवत्ता',
    agri_news: 'कृषि समाचार',
    loading_news: 'नवीनतम रीयल-टाइम समाचार लोड हो रहे हैं...',
    email: 'ईमेल',
    password: 'पासवर्ड',
    name: 'नाम',
    phone: 'फोन',
    occupation: 'पेशा',
    login_button: 'लॉगिन',
    register_button: 'पंजीकरण',
    new_user: 'नया उपयोगकर्ता? यहां पंजीकरण करें',
    existing_user: 'पहले से खाता है? लॉगिन करें',
  },
  ta: {
    features: 'அம்சங்கள்',
    farming_techniques: 'விவசாய நுட்பங்கள்',
    soil_info: 'மண் தகவல்',
    marketplace: 'வாங்க/விற்க தளம்',
    contact: 'தொடர்பு',
    login: 'உள்நுழைக',
    register: 'பதிவு செய்',
    language_selector: 'மொழி',
    english: 'ஆங்கிலம்',
    spanish: 'ஸ்பானிய',
    french: 'பிரெஞ்சு',
    hindi: 'இந்தி',
    tamil: 'தமிழ்',
    maithili: 'மைதிலி',
    profile: 'சுயவிவரம்',
    logout: 'வெளியேறு',
    hero_title: 'GreenIQ க்கு வரவேற்கிறோம்',
    hero_subtitle: 'நவீன வேளாண்மையில் உங்கள் நம்பகமான பங்குதாரி. நுண்ணறிவு பெறுங்கள், எங்கள் கருவிகளைப் பயன்படுத்தவும் மற்றும் புத்திசாலியாக வளரவும்.',
    learn_more: 'மேலும் அறிக',
    about_title: 'பயிர்கள் மற்றும் வேளாண்மை பற்றி',
    about_text: 'விவசாயம் நமது சமூகத்தின் முதுகெலும்பு ஆகும், இது லட்சக்கணக்கான மக்களுக்கு உணவு, மூலப்பொருட்கள் மற்றும் வேலைவாய்ப்பை வழங்குகிறது.',
    about_list_1: 'பல்வேறு பயிர்கள் மற்றும் அவற்றின் தேவைகள் பற்றி அறிக',
    about_list_2: 'நிலையான வேளாண்மை நுட்பங்களைக் கண்டறிக',
    about_list_3: 'நிபுணர்கள் மற்றும் பிற விவசாயிகளுடன் இணைக',
    platform_features: 'தளம் அம்சங்கள்',
    ai_assistant: 'AI உதவியாளர்',
    ai_assistant_desc: 'எங்கள் AI-இயக்கத்தவ்வாக்கப் பட்ட உதவியாளரிடமிருந்து உங்கள் வேளாண்மை கேள்விகளுக்கு ব்যক்তிগত ஆலோசனை மற்றும் பதில்களைப் பெறுங்கள்.',
    marketplace_title: 'சந்தை',
    marketplace_desc: 'நுகர்வோர் மற்றும் வணிகங்களுக்கு நேரடியாக உங்கள் பொருட்களை வாங்க மற்றும் விற்கும் ஒரு தளம்.',
    weather: 'வானிலை',
    search_city: 'ஒரு நகரத்தைத் தேடுங்கள்...',
    temperature: 'வெப்பநிலை',
    humidity: 'ஈரப்பதம்',
    wind: 'காற்றின் வேகம்',
    aqi: 'காற்றுத் தரம்',
    agri_news: 'வேளாண் செய்திகள்',
    loading_news: 'சமீபத்திய நிகழ் நேர செய்திகள் ஏற்றுவதில்...',
    email: 'மின்னஞ்சல்',
    password: 'கடவுச்சொல்',
    name: 'பெயர்',
    phone: 'தொலைபேசி',
    occupation: 'தொழிலை',
    login_button: 'உள்நுழைக',
    register_button: 'பதிவு செய்',
    new_user: 'புதிய பயனர்? இங்கே பதிவு செய்',
    existing_user: 'ஏற்கனவே கணக்கு உள்ளதா? உள்நுழைக',
  },
  mai: {
    features: 'विशेषता',
    farming_techniques: 'खेती के तकनीक',
    soil_info: 'मिट्टी की जानकारी',
    marketplace: 'खरीद/विक्रय मंच',
    contact: 'संपर्क',
    login: 'लॉगिन',
    register: 'रजिस्टर',
    language_selector: 'भाषा',
    english: 'अंग्रेजी',
    spanish: 'स्पेनिश',
    french: 'फ्रेंच',
    hindi: 'हिंदी',
    tamil: 'तमिल',
    maithili: 'मैथिली',
    profile: 'प्रोफाइल',
    logout: 'लॉगआउट',
    hero_title: 'GreenIQ मे आपक स्वागत',
    hero_subtitle: 'आधुनिक खेती मे आपक विश्वस्त साथी। अंतरदृष्टि प्राप्त करू, हमर उपकरण क प्रयोग करु आ बुद्धिमत्ताक संग बढ़ू।',
    learn_more: 'और जानू',
    about_title: 'फसल आ खेती बिसय मे',
    about_text: 'खेती हमर समाजक रीढ़ अछि जे लाखो मनुष्य के भोजन, कच्चा माल आ काज देत अछि।',
    about_list_1: 'विभिन्न फसल आ हुनका आवश्यकता बिसय मे जानु',
    about_list_2: 'टिकाऊ खेती के तकनीक खोजु',
    about_list_3: 'विशेषज्ञ आ अन्य किसान संग जुड़ु',
    platform_features: 'मंच के विशेषता',
    ai_assistant: 'एआई सहायक',
    ai_assistant_desc: 'हमर एआई-संचालित सहायक से आपक खेती प्रश्न के लेल व्यक्तिगत सलाह आ उत्तर पाउ।',
    marketplace_title: 'बाजार',
    marketplace_desc: 'आपक उपज के सीधे उपभोक्ता आ व्यवसाय के खरीद-बिक्रय के लेल एक मंच।',
    weather: 'मौसम',
    search_city: 'एक शहर खोजू...',
    temperature: 'तापमान',
    humidity: 'नमी',
    wind: 'हवा के गति',
    aqi: 'हवा के गुणवत्ता',
    agri_news: 'खेती के समाचार',
    loading_news: 'नवीनतम रियल-टाइम समाचार लोड हो रहल अछि...',
    email: 'ईमेल',
    password: 'पासवर्ड',
    name: 'नाम',
    phone: 'फोन',
    occupation: 'पेशा',
    login_button: 'लॉगिन',
    register_button: 'रजिस्टर',
    new_user: 'नया उपयोगकर्ता? यहां रजिस्टर करू',
    existing_user: 'पहले से खाता अछि? लॉगिन करू',
  },
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved as Language) || 'en';
  });

  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations['en']] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
